// La lista baja sola y lo de arriba se queda quieto.
//
// Lissette, 2026-09-25: *"si la lista tanto de clientes, polizas, renewal,
// pagos del mes, prospecto se puede bajar como se baja el menu principal sin
// que se baje el panel de arriba"*. Eligio que se quede fijo TODO lo de arriba
// (titulo, cuadritos, filtros, buscador).
//
// Como: esta caja mide cuanto espacio queda desde donde empieza hasta el borde
// de abajo de la pantalla y se da esa altura con su propio scroll — lo mismo
// que hace el menu de la izquierda. La pagina entonces ya no baja, y lo de
// arriba no se mueve. Los titulos de las columnas de una tabla se quedan
// pegados arriba de la caja (dkt.css, `.dkt-caja-scroll thead th`).
//
// Se vuelve a medir cuando cambia algo de arriba (se abren los filtros, sale
// un aviso) y al cambiar el tamaño de la ventana.
//
// SOLO en pantalla ancha (>= 1024px, donde el menu tambien va fijo). En el
// telefono la pagina entera tiene que bajar como una web normal — ver el
// "core mobile fix" de dkt.css: una caja con scroll propio ahi se atasca y la
// barra del navegador no se esconde.
import { useLayoutEffect, useState } from 'react';

const ANCHA = '(min-width: 1024px)';

/**
 * Cuanto alto le queda a una caja hasta el borde de abajo de la pantalla, o
 * null en pantalla estrecha (ahi no se limita nada). Devuelve [ref, alto]:
 * `ref` es un ref de callback ESTABLE — la caja puede aparecer mas tarde
 * (cuando cargan los datos) y se mide en cuanto aparece.
 * `reserva` son los px que hay que dejar libres debajo para lo que va DESPUES
 * de la caja (la paginacion). Sirve tambien para una caja que ya tenia scroll
 * propio (Pagos del Mes, Prospectos): se le cambia la altura fija por esta.
 */
export function useAltoHastaAbajo(reserva = 0) {
  const [el, setEl] = useState(null);
  const [alto, setAlto] = useState(null);
  useLayoutEffect(() => {
    if (!el || typeof window === 'undefined') { setAlto(null); return undefined; }
    const mq = window.matchMedia ? window.matchMedia(ANCHA) : null;
    const medir = () => {
      if (!mq || !mq.matches) { setAlto(null); return; }
      // Distancia desde el principio de la PAGINA (no de lo que se ve), para
      // que dé lo mismo si la pagina ya estaba un poco bajada.
      const arriba = el.getBoundingClientRect().top + window.scrollY;
      // 24px de aire abajo (el relleno de abajo de la pagina); nunca menos de 320px de lista, aunque lo de
      // arriba sea muy alto en una pantalla baja (entonces baja la pagina un
      // poco, que es mejor que una lista de tres filas).
      setAlto(Math.max(320, Math.floor(window.innerHeight - arriba - 24 - reserva)));
    };
    medir();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    const contenedor = el.closest('.dkt-inner') || document.body;
    if (ro) ro.observe(contenedor);
    window.addEventListener('resize', medir);
    if (mq && mq.addEventListener) mq.addEventListener('change', medir);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', medir);
      if (mq && mq.removeEventListener) mq.removeEventListener('change', medir);
    };
  }, [el, reserva]);
  return [setEl, alto];
}

export function CajaConScroll({ children }) {
  const [ref, alto] = useAltoHastaAbajo();
  return (
    <div
      ref={ref}
      className={alto ? 'dkt-caja-scroll on' : 'dkt-caja-scroll'}
      style={alto ? { maxHeight: alto, overflow: 'auto' } : undefined}
    >
      {children}
    </div>
  );
}

/**
 * Para un menu que se abre HACIA ABAJO dentro de una fila (estado, encargado,
 * etiquetas): dentro de la caja, el de la ultima fila quedaba cortado por el
 * borde de abajo. Puesto como `ref`, al abrirse mueve la caja lo justo para
 * que se vea entero. Fuera de una caja no hace nada que se note: si el menu ya
 * se ve, `nearest` no mueve nada.
 */
export function verMenuEntero(nodo) {
  if (nodo && nodo.closest && nodo.closest('.dkt-caja-scroll.on')) {
    nodo.scrollIntoView({ block: 'nearest' });
  }
}
